import { useCallback, useEffect, useId, useRef, useState } from "react";

/**
 * Anchored popover for top-bar menus/panels: toggle button with `aria-expanded`, Escape and
 * outside click close it, focus moves into the panel on open and back to the trigger on close.
 */
export function usePopover({ keepOpenOnAlertDialog = false }: { keepOpenOnAlertDialog?: boolean } = {}): {
  open: boolean;
  setOpen: (o: boolean) => void;
  triggerProps: {
    ref: React.RefObject<HTMLButtonElement | null>;
    "aria-expanded": boolean;
    "aria-controls": string;
    onClick: () => void;
  };
  panelProps: {
    ref: React.RefObject<HTMLDivElement | null>;
    id: string;
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
} {
  const [open, setOpenState] = useState(false);
  const id = useId();
  const trigger = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);

  const setOpen = useCallback((o: boolean) => {
    setOpenState(o);
    if (!o) trigger.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const first = panel.current?.querySelector<HTMLElement>(
      "button:not([disabled]), input:not([disabled]), select, textarea, [tabindex='0']",
    );
    (first ?? panel.current)?.focus();
    const onDown = (e: PointerEvent): void => {
      const t = e.target as Node;
      if (panel.current?.contains(t) || trigger.current?.contains(t)) return;
      // A confirmation can be portaled outside the initiating panel. Its reply belongs there.
      if (keepOpenOnAlertDialog && t instanceof Element && t.closest('[role="alertdialog"]')) return;
      setOpenState(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open, keepOpenOnAlertDialog]);

  return {
    open,
    setOpen,
    triggerProps: {
      ref: trigger,
      "aria-expanded": open,
      "aria-controls": id,
      onClick: () => {
        setOpenState((o) => !o);
      },
    },
    panelProps: {
      ref: panel,
      id,
      onKeyDown: (e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          setOpen(false);
        }
      },
    },
  };
}
